import CryptoJS from "crypto-js";
import { EmployeeDetails } from "../components/PayslipForm";

const DB_NAME = "PayslipGeneratorDB";
const STORE_NAME = "payslips";
const USER_STORE = "users"; // New store for credentials
const DB_VERSION = 2; // Incremented version to add new store
const SECRET_KEY = "payslip-secure-key-local";

export interface HistoryItem {
  id: string;
  createdAt: number;
  encryptedDetails: string;
  letterhead: string | null;
  signature: string | null;
  primaryColor: string;
  contentMargin: number;
  signaturePadding: number;
}

export const initDB = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject("Error opening database");

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(USER_STORE)) {
        db.createObjectStore(USER_STORE, { keyPath: "email" });
      }
    };
    request.onsuccess = () => resolve();
  });
};

// --- AUTH FUNCTIONS ---
export const registerUser = (email: string, password: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const hashedPassword = CryptoJS.SHA256(password).toString();
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onsuccess = () => {
      const db = request.result;
      const tx = db.transaction(USER_STORE, "readwrite");
      const store = tx.objectStore(USER_STORE);
      const checkReq = store.get(email);
      checkReq.onsuccess = () => {
        if (checkReq.result) return reject("User already exists");
        store.add({ email, password: hashedPassword });
        resolve();
      };
    };
  });
};

export const loginUserDB = (email: string, password: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const hashedPassword = CryptoJS.SHA256(password).toString();
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onsuccess = () => {
      const db = request.result;
      const tx = db.transaction(USER_STORE, "readonly");
      const store = tx.objectStore(USER_STORE);
      const getReq = store.get(email);
      getReq.onsuccess = () => {
        const user = getReq.result;
        resolve(user && user.password === hashedPassword);
      };
      getReq.onerror = () => resolve(false);
    };
  });
};

// --- EXISTING HISTORY FUNCTIONS (UNTOUCHED) ---
const encryptData = (data: EmployeeDetails): string => {
  return CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
};

const decryptData = (ciphertext: string): EmployeeDetails => {
  const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
  return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
};

export const savePayslipToDB = (
  details: EmployeeDetails,
  letterhead: string | null,
  signature: string | null,
  primaryColor: string,
  contentMargin: number,
  signaturePadding: number
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const encrypted = encryptData(details);
    const newItem: HistoryItem = {
      id: crypto.randomUUID(),
      createdAt: Date.now(),
      encryptedDetails: encrypted,
      letterhead,
      signature,
      primaryColor,
      contentMargin,
      signaturePadding,
    };
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onsuccess = () => {
      const db = request.result;
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).put(newItem);
      tx.oncomplete = () => resolve();
    };
  });
};

export const getAllPayslips = (): Promise<{ item: HistoryItem; decryptedDetails: EmployeeDetails }[]> => {
  return new Promise((resolve) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onsuccess = () => {
      const db = request.result;
      const tx = db.transaction(STORE_NAME, "readonly");
      const getAllRequest = tx.objectStore(STORE_NAME).getAll();
      getAllRequest.onsuccess = () => {
        const results = getAllRequest.result as HistoryItem[];
        const decryptedResults = results.map(item => ({
          item,
          decryptedDetails: decryptData(item.encryptedDetails)
        }));
        decryptedResults.sort((a, b) => b.item.createdAt - a.item.createdAt);
        resolve(decryptedResults);
      };
    };
  });
};

export const deletePayslipFromDB = (id: string): Promise<void> => {
  return new Promise((resolve) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onsuccess = () => {
      const db = request.result;
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).delete(id);
      tx.oncomplete = () => resolve();
    };
  });
};